import { LicensePlate } from './Vehicle';

import type { GrpcCurrencyObject } from './Common';
import type { Location } from './Location';
import type { Site } from './Site';
import type {
  CreateDollarDiscountValidation,
  CreateFlatRateValidation,
  CreateFreeMinutesBasedOnWeekdayValidation,
  CreateFreeMinutesValidation,
  CreatePercentDiscountValidation,
  CreateTabularValidation,
  NewTabularDiscountForm,
  TabularDiscountType,
  ValidationPlan,
  ValidationProductType,
  ValidationType,
} from './Validation';

export type ValidationPlanForm =
  | CreateFreeMinutesValidation
  | CreatePercentDiscountValidation
  | CreateDollarDiscountValidation
  | CreateFreeMinutesBasedOnWeekdayValidation
  | CreateFlatRateValidation
  | CreateTabularValidation;

// Unfortunately, the endpoint to create a validation sign
// has a different shape than the validation data used elsewhere
// hence the need for this more flexible type for use in the
// Enterprise signage form
export interface ValidationSignFormDefaults {
  allowMaxExceeded?: boolean | string;
  cooldownMins?: number | string;
  dollarAmount?: number | string;
  effectiveFrom?: string;
  freeMins?: number | string;
  percent?: number | string;
  planType?: ValidationType;
  tabularDiscountType?: TabularDiscountType;
  tabularSteps?: {
    cumulativeDurationMinutes: number | string;
    discountAmount: number | string;
  }[];
  type?: ValidationType;
  weekdayMaxMinutes?: number | string;
  weekendMaxMinutes?: number | string;
}
export type EnterpriseAdmin = {
  email?: string;
  firstName?: string;
  lastName?: string;
  phone1?: string;
  phone2?: string;
};

export type EnterpriseTaxExemption = {
  isExempt?: boolean;
  taxExemptionNumber?: string;
};

export type Enterprise = {
  active?: boolean;
  addedAt?: number;
  address1?: string;
  admin: EnterpriseAdmin;
  autoPay: boolean;
  billing?: EnterpriseAdmin;
  city?: string;
  id: number;
  memberCount: number | null;
  name: string;
  operationsOwner: string;
  operatorId?: number;
  operatorUUID?: string;
  state?: string;
  taxExemption?: EnterpriseTaxExemption;
  uuid?: string;
  zip?: string;
};

export type SiteHardMax = {
  hardMax: number;
  siteId: number;
  softMax?: number;
};

export type CreateEnterpriseSubscriptionRequestBody = {
  baseRateMonthly: number;
  billingStartAt: number;
  maximum: number;
  minimum: number; // min and max num of vehicles
  paymentCollectorSiteId?: number;
  siteHardMaxes?: SiteHardMax[];
  siteIds: number[];
};

export type EnterpriseSubscriptionDetail = {
  details: {
    addedAt: number;
    baseRateMonthly: number;
    billingStartAt: number;
    effectiveFrom: number;
    enterpriseSubscriptionPlanId: number;
    maxVehicles: number;
    minVehicles: number;
    paymentCollectorSiteId: number;
    planType: string; // TODO: create type for this
    siteId: number;
    siteIds: number[];
  };
  id: number;
  memberCount: number;
  subscribers: EntSubMember[];
};

export type EntSubUpdatePayload = {
  baseRateMonthly: number;
  billingStartAt?: number;
  maximum: number;
  minimum: number;
  paymentCollectorSiteId?: number;
  siteHardMaxes?: SiteHardMax[];
};

export type EntSubRedemptionCode = {
  redemptionCode: {
    addedAt: number;
    effectiveFrom: number;
    id: number;
    secret: string;
  };
  subscriptionRedemptionCode: {
    addedAt: number;
    id: number;
    redemptionCodeId: number;
    subscriptionId: number;
  };
};

export type EntSubMember = {
  accessEnd?: number;
  accessStart?: number;
  addedToSub: number;
  email: string | null;
  firstName: string | null;
  id: number; // this is the same as userId
  lastName: string | null;
  licensePlates: LicensePlate[];
  phone: string | null;
};

export type EntSubListItem = {
  activeRedemptionCodes: EntSubRedemptionCode[];
  enterpriseSubscription: EnterpriseSubscriptionDetail;
  memberCount: number;
  siteHardMaxes?: SiteHardMax[];
  sites: Location[];
};

export type EntSubDetail = {
  enterpriseSubscription: EnterpriseSubscriptionDetail;
  redemptionCodes: {
    active: EntSubRedemptionCode[];
    expired: EntSubRedemptionCode[];
  };
  sites: Site[];
};

export type CombinedSubscriptionDetail = EnterpriseSubscriptionDetail &
  Partial<EntSubRedemptionCode> & {
    allowedBillingActions: EntSubBillingAllowedActions;
    billingEnrollmentState: EntSubBillingEnrollmentState;
    id: number;
    siteHardMaxes?: SiteHardMax[];
    subscribers: EntSubMember[];
    subSites?: EntSubSiteDetailSimple[];
  };

export interface CreateEntLocationPayload {
  address: string;
  city: string;
  enterpriseLocationCapacityId: number | null;
  enterpriseLocationTypeIds: number[];
  name: string;
  phoneNumber: string;
  state: string;
  zip: string;
}

export type EntLocationCapacity = {
  addedAt: number;
  id: number;
  name: string;
};

export type EntLocationType = {
  addedAt: number;
  id: number;
  name: string;
};

export interface EntLocation {
  addedAt: number;
  address: string;
  capacity?: {
    addedAt: number;
    id: number;
    name: string;
  };
  city: string;
  enterpriseId: number;
  id: number;
  name: string;
  phoneNumber: string;
  state: string;
  types: {
    addedAt: number;
    id: number;
    name: string;
  }[];
  zip: string;
}

export interface EntLocSignData {
  addedAt: number;
  id: number;
  name: string;
}

export type EntLocSignDataMap = {
  [key: number]: EntLocSignData;
};

export type EntLocSignNameMap = {
  [key: string]: EntLocSignData;
};

export interface EntLocSign {
  addedAt: number;
  id: number;
  qrCodeValidation: {
    content: string;
    qrCodeId: number;
    qrCodeImage: string;
    signCode: string;
    siteIds: number[];
    siteNames: string[];
    validation: {
      active: boolean;
      addedAt: number;
      cooldownMins: number | null;
      effectiveFrom: number | null;
      expiresAt: number | null;
      id: number;
      maxRedemptions: number | null;
      name: string | null;
      partiallyDiscountable: boolean;
      updatedAt: number;
      validationName?: string; // todo: this doesn't exist yet. BE needs to add
      validationPlan: ValidationPlan;
      validationStatus: string;
      vendorName: string;
    };
  };
  quantity: number;
  signType: {
    addedAt: number;
    id: number;
    name: string;
  };
}

export type SignMutationPayload = {
  entId: number;
  locId: number;
  payload: CreateEntLocSignPayload;
  signId: number;
};

export interface CreateEntLocSignPayload {
  quantity: number;
  signTypeId: number;
  siteIds: number[];
  validationForm: {
    cooldownMins?: number;
    effectiveFrom?: number; // millis
    expiresAt?: number; // millis
    maxRedemptions?: number;
    name?: string;
    partiallyDiscountable: boolean;
    plan: ValidationPlanForm | NewTabularDiscountForm;
    productType?: ValidationProductType;
    vendorName: string;
  };
}

export interface EntSubInvoiceItem {
  activeMemberCount: number;
  additionalProratedAmount: number;
  effectiveDate?: number;
  maximumMembers: number;
  minimumMembers: number;
  proratedMemberCount: number;
  ratePerMemberAmount: number;
  siteName: string;
  siteNames: string[];
  totalRateAmount: number;
}

export interface EntSubBillingEnrollmentState {
  nextChargeDate?: string;
  status: string;
}

export interface EntSubBillingAllowedActions {
  updateBillingStartAt: boolean;
}

export interface EnterpriseAdminFlags {
  canCreateMultiSiteSubscriptions: boolean;
}

export type PaymentStatus = {
  id: number;
  name: string;
};

export type MetaData =
  | {
      values: {
        intervalEnd: string;
        intervalStart: string;
        item?: string;
        locations: string;
        maxVehicles: string;
        minVehicles: string;
        newLineItemServiceFeeId?: string;
        paymentCollectorSiteId: string;
        paymentCollectorSiteIdFullLabel?: string;
        rateType?: string;
        rawCustomerAmountCentsTotal: string;
      };
    }
  | {
      values: {
        feeId?: string;
      };
    };

export interface InvoiceLineItemType {
  addedAt: string;
  additionalInfo: {
    section: string;
    table: {
      columnHeaders: [string, string, string];
      rows: {
        columnValues: [string, string, string];
      }[];
    };
  };
  amount: GrpcCurrencyObject;
  description: string;
  idempotencyKey: string;
  invoiceLineItemId: string;
  isNew?: boolean;
  lineItemType: 'FEE' | 'CHARGE' | 'PRORATION';
  metadata: MetaData;
  productId: string;
  productType: string;
  quantity: number;
  total: GrpcCurrencyObject;
}

// newer
export interface EnterpriseInvoiceServiceInvoice {
  invoiceDate: string;
  invoiceId: string;
  invoiceLineItems: InvoiceLineItemType[];
  invoiceNumber: string;
  metadata: {
    collectedAmount?: GrpcCurrencyObject;
    paymentIds?: string[];
    updatedBy?: {
      userId?: number;
      userUuid?: string;
    };
    values?: {
      memoNote?: string;
      paymentCollectorSiteId?: string;
      paymentCollectorSiteIdFullLabel?: string;
      paymentNote?: string;
      statusNote?: string;
    };
  };
  paymentId: string;
  paymentStatus: string;
  replacedById: string;
  replacesId: string;
  status: InvoiceStatus;
  total: GrpcCurrencyObject;
}

export type EntSubSiteDetailSimple = {
  address_1: string;
  city: string;
  id: number;
  name: string;
  stateCode: string;
  uuid?: string;
  zipcode: string;
};

export type EntInvoicePaymentDetails = {
  payment: {
    addedAt: string;
    amount: GrpcCurrencyObject;
    details: string;
    errorResponse: string;
    errors: {
      code: string;
      detail: string;
    }[];
    id: {
      value: string;
    };
    metadata: { [key: string]: string };
    paymentMethodUuid: string;
    status: string;
    stripePaymentIntentId: string;
    uuid: string;
  };
  paymentMethod?: {
    achTransferSource?: {
      achAccountNumber: string;
      achRoutingNumber: string;
      bankName: string;
      last4Digits: string;
    };
    bankAccount?: {
      bankName: string;
      last4Digits: string;
    };
    card?: {
      brand: string;
      expiryMonth: number;
      expiryYear: number;
      last4Digits: string;
    };
  };
};

export type EntInvoicePaymentsDetails = {
  achCreditInstructions: {
    achAccountNumber: string;
    achRoutingNumber: string;
    bankName: string;
    last4Digits: string;
  };
  addedAt: string;
  allowedPaymentMethodTypes: string[];
  amount: GrpcCurrencyObject;
  capturedAmount?: GrpcCurrencyObject;
  metadata: { [key: string]: string };
  paymentId: string;
  status: string;
};

// todo: move all invoice related types to its own file
export enum InvoiceStatus {
  VOID = 'VOID',
  DRAFT = 'DRAFT',
  PAID = 'PAID',
  OPEN = 'OPEN',
  UNCOLLECTIBLE = 'UNCOLLECTIBLE',
  PARTIAL = 'PARTIALLY FUNDED',
  PASTDUE = 'PAST DUE',
}
